import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { BrandLoadingScreen } from '@/components/shared/BrandLoadingScreen'

interface ProtectedRouteProps {
  children: ReactNode
}

/**
 * Guards routes that require an authenticated session.
 *
 * Waits for both:
 *   - `initialized` — INITIAL_SESSION fired (user/null known, ~ms from localStorage)
 *   - `loading`     — profile DB fetch complete (needed for role-based routing)
 *
 * Redirects to /login if there is no authenticated user, preserving the
 * intended destination so login can redirect back.
 */
function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, initialized } = useAuth()
  const location = useLocation()

  // initialized flips almost immediately (INITIAL_SESSION from localStorage).
  // loading flips after the profile DB fetch — instant on cache hit.
  if (!initialized || loading) {
    return <BrandLoadingScreen />
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

export { ProtectedRoute }
export type { ProtectedRouteProps }
